import {useEffect, useState} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {
  createStockTransfer,
  getWarehouseOptions,
  getProductOptions,
  getCurrentInventoryMetrics,
  WarehouseOption,
  ProductOption,
} from '../api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {InventoryStateSummary} from '../../inventory/components/InventoryStateSummary'
import {removeIndicatorSeparator, solidSelectStyles} from '../../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const stockTransferSchema = Yup.object().shape({
  source_warehouse_id: Yup.number()
    .min(1, 'Source warehouse is required')
    .required('Source warehouse is required'),
  destination_warehouse_id: Yup.number()
    .min(1, 'Destination warehouse is required')
    .required('Destination warehouse is required')
    .test(
      'not-same-warehouse',
      'Source and destination warehouses cannot be the same',
      function (value) {
        return value !== this.parent.source_warehouse_id
      }
    ),
  product_id: Yup.number().min(1, 'Product is required').required('Product is required'),
  quantity: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
  transfer_date: Yup.string().required('Transfer date is required'),
  remarks: Yup.string(),
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface StockTransferFormValues {
  source_warehouse_id: number
  destination_warehouse_id: number
  product_id: number
  quantity: number
  transfer_date: string
  remarks: string
}

type OptionType = {
  value: number
  label: string
}

const getTodayDate = () => new Date().toISOString().split('T')[0]

// ─── Component ────────────────────────────────────────────────────────────────

export const StockTransfer: React.FC = () => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)
  const [warehouseOptions, setWarehouseOptions] = useState<OptionType[]>([])
  const [productOptions, setProductOptions] = useState<OptionType[]>([])
  const [sourceWarehouse, setSourceWarehouse] = useState<OptionType>({
    label: 'Select Source Warehouse...',
    value: 0,
  })
  const [destWarehouse, setDestWarehouse] = useState<OptionType>({
    label: 'Select Destination Warehouse...',
    value: 0,
  })
  const [product, setProduct] = useState<OptionType>({label: 'Select Product...', value: 0})
  const [availableStock, setAvailableStock] = useState<number>(0)
  const [valuationPerUnit, setValuationPerUnit] = useState<number>(0)

  const initialValues: StockTransferFormValues = {
    source_warehouse_id: 0,
    destination_warehouse_id: 0,
    product_id: 0,
    quantity: 0,
    transfer_date: getTodayDate(),
    remarks: '',
  }

  const formik = useFormik<StockTransferFormValues>({
    initialValues,
    validationSchema: stockTransferSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (values.quantity > availableStock) {
        toast.error('Quantity exceeds available stock in the source warehouse.')
        return
      }
      setLoading(true)
      const body = {
        stock_transfer_id: null,
        company_id: Number(company?.value) || null,
        source_warehouse_id: values.source_warehouse_id,
        destination_warehouse_id: values.destination_warehouse_id,
        product_id: values.product_id,
        quantity: values.quantity,
        transfer_date: values.transfer_date,
        remarks: values.remarks || '',
      }
      await createStockTransfer(body)
        .then((res) => {
          res?.detail && toast.success('Stock Transfer has been recorded successfully.')
          formik.resetForm()
          setSourceWarehouse({label: 'Select Source Warehouse...', value: 0})
          setDestWarehouse({label: 'Select Destination Warehouse...', value: 0})
          setProduct({label: 'Select Product...', value: 0})
          setAvailableStock(0)
          setValuationPerUnit(0)
        })
        .catch((err) => toast.error(err?.detail || err.message))
        .finally(() => setLoading(false))
    },
  })

  // Load warehouse and product options
  useEffect(() => {
    const loadOptions = async () => {
      if (!company?.value) return

      const [whRes, prodRes] = await Promise.all([
        getWarehouseOptions(company.value),
        getProductOptions(company.value),
      ])
      if (whRes) {
        setWarehouseOptions(
          whRes.map((w: WarehouseOption) => ({label: w.warehouse_name, value: w.warehouse_id}))
        )
      }
      if (prodRes) {
        setProductOptions(
          prodRes.map((p: ProductOption) => ({label: p.product_name, value: p.product_id}))
        )
      }
    }

    loadOptions()
  }, [company?.value])

  // Sync selects back into formik
  useEffect(() => {
    formik.setFieldValue('source_warehouse_id', sourceWarehouse.value, true)
    formik.setFieldValue('destination_warehouse_id', destWarehouse.value, true)
    formik.setFieldValue('product_id', product.value, true)
  }, [sourceWarehouse.value, destWarehouse.value, product.value])

  // Calculate available stock from source warehouse + product
  useEffect(() => {
    const calculateAvailableStock = async () => {
      if (!company?.value || !sourceWarehouse.value || !product.value) {
        setAvailableStock(0)
        setValuationPerUnit(0)
        return
      }
      try {
        const metrics = await getCurrentInventoryMetrics(
          company.value,
          Number(sourceWarehouse.value),
          Number(product.value)
        )
        if (metrics) {
          setAvailableStock(Number(metrics.available_stock || 0))
          setValuationPerUnit(Number(metrics.valuation_per_unit || 0))
        }
      } catch {
        setAvailableStock(0)
        setValuationPerUnit(0)
      }
    }
    calculateAvailableStock()
  }, [sourceWarehouse.value, product.value, company?.value])

  return (
    <KTCard>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Stock Transfer</h3>
        </div>
      </div>

      <KTCardBody className='py-4'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          {/* Source + Destination Warehouse */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>
              Source Warehouse
            </label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={sourceWarehouse}
                onChange={(option: any) => setSourceWarehouse(option as OptionType)}
                options={warehouseOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>
              Destination Warehouse
            </label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={destWarehouse}
                onChange={(option: any) => setDestWarehouse(option as OptionType)}
                options={warehouseOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
              {formik.touched.destination_warehouse_id && formik.errors.destination_warehouse_id && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.destination_warehouse_id}</div>
                </div>
              )}
            </div>
          </div>

          {/* Product */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Product</label>
            <div className='col-lg-4 fv-row'>
              <Select
                value={product}
                onChange={(option: any) => setProduct(option as OptionType)}
                options={productOptions}
                isSearchable={true}
                className='fw-bold'
                styles={solidSelectStyles}
                components={removeIndicatorSeparator}
              />
            </div>
          </div>

          <InventoryStateSummary
            stockLabel='Available Stock'
            availableStock={availableStock}
            valuationPerUnit={valuationPerUnit}
          />

          {/* Quantity + Transfer Date */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Quantity</label>
            <div className='col-lg-4 fv-row'>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                placeholder='Quantity'
                {...formik.getFieldProps('quantity')}
              />
              {formik.touched.quantity && formik.errors.quantity && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.quantity}</div>
                </div>
              )}
            </div>

            <label className='col-lg-2 col-form-label required fw-bold fs-6'>Transfer Date</label>
            <div className='col-lg-4 fv-row'>
              <input
                type='date'
                className='form-control form-control-lg form-control-solid'
                {...formik.getFieldProps('transfer_date')}
              />
              {formik.touched.transfer_date && formik.errors.transfer_date && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.transfer_date}</div>
                </div>
              )}
            </div>
          </div>

          {/* Remarks */}
          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label fw-bold fs-6'>Remarks</label>
            <div className='col-lg-10 fv-row'>
              <textarea
                className='form-control form-control-lg form-control-solid'
                placeholder='Reference / Remarks'
                rows={3}
                {...formik.getFieldProps('remarks')}
              />
            </div>
          </div>

          <div className='card-footer d-flex justify-content-start px-0 border-0 pb-0 pt-4'>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={loading || !formik.isValid || formik.values.quantity > availableStock}
            >
              {!loading ? 'Transfer Stock' : null}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </KTCardBody>
    </KTCard>
  )
}
